import React, { FC, useMemo } from 'react';
import Stack from '@mui/material/Stack';
import moment from 'moment-timezone';
import { useSelector } from 'react-redux';

import { IDepartmentScheduleDTO, IScheduledAbsenceDTO, IShiftScheduleDTO } from 'src/api/dtos';
import { Modal, Status } from 'src/shared/components';
import { DAY_DATE_FORMAT } from 'src/shared/constants';
import { useTranslate } from 'src/shared/providers';
import { getTimeFormat } from 'src/store/user/user.selectors';

import { CalenderMySchedule, CalendarPeerSchedules } from './modal-content';

interface Props {
  departmentSchedules: IDepartmentScheduleDTO[];
  employeeSchedule: IShiftScheduleDTO | null;
  scheduledAbsence: IScheduledAbsenceDTO | null;
  open: boolean;
  onClose: () => void;
}

export const CalendarDepartmentScheduleModal: FC<Props> = ({
  employeeSchedule,
  departmentSchedules,
  scheduledAbsence,
  open,
  onClose,
}) => {
  const { t } = useTranslate();
  const timeFormat = useSelector(getTimeFormat);

  const formattedStartDate = moment(employeeSchedule?.startDate).format(timeFormat);
  const formattedEndDate = moment(employeeSchedule?.endDate).format(timeFormat);

  const formattedStartAbsenceDate = moment(scheduledAbsence?.startTime).format(timeFormat);
  const formattedEndAbsenceDate = moment(scheduledAbsence?.endTime).format(timeFormat);

  let schedule: string | undefined;
  if (employeeSchedule?.startDate) {
    schedule =
      formattedStartDate === formattedEndDate ? formattedStartDate : `${formattedStartDate} - ${formattedEndDate}`;
  }

  let absence: string | undefined;
  if (scheduledAbsence) {
    if (scheduledAbsence.startTime) {
      const absenceRange = `${scheduledAbsence.timeCategoryName} ${formattedStartAbsenceDate} - ${formattedEndAbsenceDate}`;
      absence = schedule ? `${schedule} (${absenceRange})` : absenceRange;
    } else {
      absence = scheduledAbsence.timeCategoryName;
    }
  }

  const date = useMemo(() => moment(departmentSchedules?.[0]?.date).format(DAY_DATE_FORMAT), [departmentSchedules]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      modalHeaderProps={{
        title: (
          <Stack direction="row" gap={2}>
            {t('calendar.schedules')}
          </Stack>
        ),
        message: date,
        startIcon: <Status size="large" variant={'info'} />,
        onClose,
      }}
    >
      <div className="ms-3 ps-3">
        {<CalenderMySchedule schedule={schedule} isUnpublished={employeeSchedule?.isUnpublished} absence={absence} />}
      </div>
      <div className="ms-3 ps-3 mt-4">{<CalendarPeerSchedules departmentSchedules={departmentSchedules} />}</div>
    </Modal>
  );
};
